import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useHistory } from 'react-router';
import DealCard from '@components/DealCard';
import { DealPostType } from '@types';
import config from '@configFile';
import { hashASIN } from '@utils/index';

const getRandomAdditionalDealString = (
  brand: string,
  numDeals: number,
  ASIN: string
) => {
  const dealsStr =
    numDeals === 1
      ? `${numDeals} Additional Deal`
      : `${numDeals} Additional Deals`;
  const dealsStrFinal = brand ? `${dealsStr} From ${brand}` : dealsStr;

  if (numDeals === 1 || !brand || !ASIN) {
    return dealsStrFinal;
  }

  const randomDealStrings = [
    `Have you seen these other ${numDeals} deals from ${brand}?`,
    `You might also like these ${numDeals} deals from ${brand}...`,
    `Don't miss these ${numDeals} deals from ${brand}...`,
    `You won't want to miss these ${numDeals} deals from ${brand}...`,
    `These ${numDeals} deals from ${brand} are worth a look.`,
    `You might be interested in these ${numDeals} deals from ${brand}...`,
    `Grab these ${numDeals} deals from ${brand} before they're gone!`,
    `Explore these ${numDeals} amazing deals from ${brand}!`,
    `Save big with these ${numDeals} fantastic deals from ${brand}!`,
    `Shopping for deals? ${numDeals} more from ${brand} await you:`,
    `Don't stop now—check out ${numDeals} more deals from ${brand}!`,
    `Love ${brand}? You'll love these ${numDeals} deals even more!`,
    `Keep the savings going with these ${numDeals} deals from ${brand}!`,
    `${brand} has ${numDeals} more deals you need to see right now:`,
    `Maximize your savings with ${numDeals} extra deals from ${brand}!`,
    `Still looking? ${numDeals} deals from ${brand} might have what you need...`,
    `The savings keep coming! ${numDeals} deals from ${brand} are here!`,
    `Take a moment to browse these ${numDeals} deals from ${brand}...`,
    `Add even more value to your cart with ${numDeals} deals from ${brand}!`,
    `You’re not done yet—${numDeals} more deals from ${brand} await!`,
    `Save more with ${numDeals} extra deals from ${brand}!`,
    `Big fan of ${brand}? Check out these ${numDeals} exclusive deals:`,
    `Boost your savings with ${numDeals} additional deals from ${brand}!`
  ];

  const index = hashASIN(ASIN) % randomDealStrings.length;

  return randomDealStrings[Math.floor(index)];
};

const RelatedDeals = ({
  relatedDeals,
  tag = config.AFFILIATE_TAGS.FEED,
  hideDealString = false,
  link,
  previewDeals = 2,
  ASIN = ''
}: {
  relatedDeals: DealPostType[];
  tag?: string;
  hideDealString?: boolean;
  link?: string;
  previewDeals?: number;
  ASIN?: string;
}) => {
  const history = useHistory();
  const [showAll, setShowAll] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const linkMode = link && relatedDeals?.length > 30;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Show only the first two deals by default
  const dealsToShow = showAll
    ? relatedDeals
    : relatedDeals.slice(0, previewDeals);

  // Handle show all button click
  const handleShowAll = () => {
    if (linkMode) {
      history.push(link);

      // smooth scroll to top after half second
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 500);
    } else {
      setShowAll(true);
    }
  };

  const currentBrand = relatedDeals[0]?.brand || '';

  const dealsStr = getRandomAdditionalDealString(
    currentBrand,
    relatedDeals.length,
    ASIN
  );
  const backgroundColor = isDarkMode ? '#212121' : '#dcdbdf';
  const borderColor = isDarkMode ? '#212121' : '#bdc6ca';

  return (
    <Box
      sx={
        hideDealString
          ? {
              position: 'relative'
            }
          : {
              position: 'relative',
              backgroundColor,
              padding: isMobile ? '8px 8px 16px 8px' : '32px 64px',
              marginLeft: '-17px',
              marginRight: '-16px',
              borderRight: `1px solid ${borderColor}`,
              borderLeft: `1px solid ${borderColor}`
            }
      }
    >
      <Box
        sx={
          hideDealString
            ? {}
            : {
                // todo: test for dark mode
                margin: '8px 8px 0 8px',
                borderRadius: '7px',
                backgroundColor: isDarkMode
                  ? theme.palette.background.default
                  : '#ffffff'
              }
        }
      >
        {hideDealString ? null : (
          <Typography
            sx={{
              paddingTop: '8px',
              paddingLeft: '8px'
            }}
            variant="subtitle1"
            gutterBottom
            component="p"
          >
            {dealsStr}
          </Typography>
        )}
        <Box sx={{ position: 'relative', padding: isMobile ? '4px' : '8px' }}>
          {dealsToShow.map((deal: DealPostType) => (
            <DealCard key={deal.ASIN} {...deal} tag={tag} shouldLazyLoad />
          ))}

          {/* Show a shadow effect over the second deal if not showing all */}
          {!showAll && relatedDeals.length > 2 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '140px',
                background: `linear-gradient(to bottom, transparent, ${
                  isDarkMode ? theme.palette.background.default : '#ffffff'
                })`,
                pointerEvents: 'none' // Don't block interaction with the button below
              }}
            />
          )}
        </Box>

        {/* Show the "Show All" button if there are more than 2 deals and we haven't shown all */}
        {!showAll && relatedDeals.length > previewDeals && (
          <Box
            padding="16px 0px 0px 0px"
            textAlign="center"
            marginBottom="16px"
          >
            <Button
              variant={linkMode ? 'contained' : 'outlined'}
              onClick={handleShowAll}
              sx={{
                marginBottom: '16px'
              }}
            >
              {linkMode
                ? `See All ${relatedDeals.length.toLocaleString()} Deals On Page`
                : `Show All Deals (${relatedDeals.length.toLocaleString()})`}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RelatedDeals;
